import { DEBUG, DEBUG_ALERT_MSG } from '../config';
import { logCrash } from './crashlytics';
import { serializeError } from './serializeError';
import { isCurrentTabMaster } from './establishMultitabRole';
import { throttle } from './schedulers';

let showError = true;
let error: Error | undefined;

window.addEventListener('error', handleErrorEvent);
window.addEventListener('unhandledrejection', handleErrorEvent);

if (DEBUG) {
  window.addEventListener('focus', () => {
    if (!isCurrentTabMaster()) {
      return;
    }
    showError = true;
    if (error) {
      // eslint-disable-next-line no-alert
      window.alert(getErrorMessage(error));
      error = undefined;
    }
  });
  window.addEventListener('blur', () => {
    if (!isCurrentTabMaster()) {
      return;
    }
    showError = false;
  });
}

const throttleError = throttle((err) => {
  if (showError) {
    // eslint-disable-next-line no-alert
    window.alert(getErrorMessage(err));
  } else {
    error = err;
  }
}, 1500);

export function handleError(
  err: Error,
  context?: { component?: string; extraInfo?: Record<string, any> },
) {
  // eslint-disable-next-line no-console
  console.error(err);

  // Отправить в Firebase Crashlytics
  logCrash(err, context).catch((crashError) => {
    // eslint-disable-next-line no-console
    console.error('[Crashlytics] Failed to log crash:', crashError);
  });

  if (DEBUG) {
    throttleError(err);
  }
}

const IGNORED_ERRORS = [
  'PASSKEY_AUTH_COMPLETE',
  'ERR_ENTERPRISE_IS_BLOCKED',
  'Disconnect',
  'Cannot send requests while disconnected',
];

function handleErrorEvent(e: ErrorEvent | PromiseRejectionEvent) {
  // https://stackoverflow.com/questions/49384120/resizeobserver-loop-limit-exceeded
  if (e instanceof ErrorEvent && e.message === 'ResizeObserver loop limit exceeded') {
    return;
  }

  // Ignore plain Events that aren't ErrorEvent or PromiseRejectionEvent
  // (e.g. CSP violations, script load failures dispatch bare Event objects)
  if (!(e instanceof ErrorEvent) && !('reason' in e)) {
    e.preventDefault();
    return;
  }

  // Ignore non-Error rejection reasons (e.g. native Event objects as rejection reason)
  if ('reason' in e && e.reason && !(e.reason instanceof Error) && typeof e.reason !== 'string') {
    e.preventDefault();
    return;
  }

  // Ignore ErrorEvents where .error is not an Error (e.g. thrown Event objects, DOM events)
  if (e instanceof ErrorEvent && e.error && !(e.error instanceof Error) && typeof e.error !== 'string') {
    e.preventDefault();
    return;
  }

  // Ignore empty ErrorEvents with no error and no message
  if (e instanceof ErrorEvent && !e.error && !e.message) {
    e.preventDefault();
    return;
  }

  const reason = e instanceof ErrorEvent ? (e.error?.message || e.message) : (e.reason?.message || '');
  if (IGNORED_ERRORS.some((msg) => reason.includes(msg))) {
    e.preventDefault();
    return;
  }

  e.preventDefault();

  let errorObj: Error;
  if (e instanceof ErrorEvent) {
    errorObj = e.error || new Error(e.message);
  } else {
    errorObj = e.reason instanceof Error ? e.reason : new Error(serializeError(e.reason));
  }

  const context = {
    component: 'GlobalHandler',
    extraInfo: {
      type: e instanceof ErrorEvent ? 'error' : 'unhandledrejection',
      filename: e instanceof ErrorEvent ? e.filename : undefined,
      lineno: e instanceof ErrorEvent ? e.lineno : undefined,
      colno: e instanceof ErrorEvent ? e.colno : undefined,
    },
  };

  handleError(errorObj, context);
}

function getErrorMessage(err: Error) {
  return `${DEBUG_ALERT_MSG}\n\n${(err?.message) || err}\n${err?.stack}`;
}
